import React, { useState, useEffect } from 'react';
import { Loader2, Link2, GripVertical } from 'lucide-react';
import { getInfo, concatColumns } from '../../utils/api';
import { toast } from 'sonner';
import { useT } from '../../i18n';
import MissingChoice from './MissingChoice';

const ConcatColumnsOperation = ({ onSuccess, onCancel }) => {
  const t = useT();
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  
  const [config, setConfig] = useState({
    columns: [],
    new_column_name: '',
    separator: ' '
  });

  useEffect(() => {
    loadColumns();
  }, []);

  const loadColumns = async () => {
    try {
      const result = await getInfo();
      setColumns(result.info.column_names || []);
    } catch (error) {
      toast.error(t('form.loadColumnsFailed'));
    } finally {
      setLoading(false);
    }
  };

  const toggleColumn = (col) => {
    setConfig(prev => ({
      ...prev,
      columns: prev.columns.includes(col)
        ? prev.columns.filter(c => c !== col)
        : [...prev.columns, col]
    }));
  };

  const moveColumn = (from, to) => {
    const newColumns = [...config.columns];
    const [removed] = newColumns.splice(from, 1);
    newColumns.splice(to, 0, removed);
    setConfig(prev => ({ ...prev, columns: newColumns }));
  };

  const handleApply = async () => {
    if (config.columns.length < 2) {
      toast.error(t('concat.needTwo'));
      return;
    }
    if (!config.new_column_name.trim()) {
      toast.error(t('concat.needName'));
      return;
    }

    try {
      setApplying(true);
      const result = await concatColumns({
        columns: config.columns,
        new_column_name: config.new_column_name.trim(),
        separator: config.separator
      });
      onSuccess(result);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('concat.failed'));
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 text-muted-foreground">
        <Link2 className="h-5 w-5" />
        <p className="text-sm">{t('concat.intro')}</p>
      </div>

      {/* Column selection */}
      <div>
        <label className="label mb-2 block">{t('concat.selectColumns')}</label>
        <p className="text-xs text-muted-foreground mb-2">{t('concat.selectHelp')}</p>
        <div className="flex flex-wrap gap-2 p-3 border border-border rounded-lg bg-muted/30 max-h-40 overflow-y-auto">
          {columns.map(col => (
            <button
              key={col}
              type="button"
              onClick={() => toggleColumn(col)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                config.columns.includes(col)
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card border border-border hover:border-primary'
              }`}
              data-testid={`concat-col-${col}`}
            >
              {config.columns.includes(col) && (
                <span className="mr-1 text-xs">#{config.columns.indexOf(col) + 1}</span>
              )}
              {col}
            </button>
          ))}
        </div>
      </div>

      {/* Selected columns order */}
      {config.columns.length > 0 && (
        <div>
          <label className="label mb-2 block">{t('concat.order')}</label>
          <div className="space-y-1">
            {config.columns.map((col, index) => (
              <div 
                key={col} 
                className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg"
              >
                <GripVertical className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{index + 1}. {col}</span>
                <div className="ml-auto flex gap-1">
                  <button
                    type="button"
                    onClick={() => index > 0 && moveColumn(index, index - 1)}
                    disabled={index === 0}
                    className="btn-ghost h-6 w-6 p-0 text-xs disabled:opacity-30"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => index < config.columns.length - 1 && moveColumn(index, index + 1)}
                    disabled={index === config.columns.length - 1}
                    className="btn-ghost h-6 w-6 p-0 text-xs disabled:opacity-30"
                  >
                    ↓
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Separator */}
      <div>
        <label className="label mb-2 block">{t('concat.separator')}</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={config.separator}
            onChange={(e) => setConfig(prev => ({ ...prev, separator: e.target.value }))}
            placeholder={t('concat.separatorPlaceholder')}
            className="input flex-1"
            data-testid="concat-separator"
          />
          <div className="flex gap-1">
            {[' ', ', ', '-', '_', ' | ', ''].map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setConfig(prev => ({ ...prev, separator: s }))}
                className={`px-3 py-2 rounded-lg text-sm font-mono border transition-colors ${
                  config.separator === s
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-muted border-border hover:border-primary'
                }`}
              >
                {s === '' ? '(none)' : s === ' ' ? '␣' : `"${s}"`}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* New column name */}
      <div>
        <label className="label mb-2 block">{t('concat.nameLabel')}</label>
        <input
          type="text"
          value={config.new_column_name}
          onChange={(e) => setConfig(prev => ({ ...prev, new_column_name: e.target.value }))}
          placeholder={t('concat.namePlaceholder')}
          className="input"
          data-testid="concat-new-name"
        />
      </div>

      {/* Preview */}
      {config.columns.length >= 2 && (
        <div className="p-4 bg-muted/50 rounded-lg">
          <p className="text-sm text-muted-foreground">
            <strong>{t('concat.previewLabel')}</strong> {config.columns.join(` ${config.separator === '' ? '+' : `"${config.separator}"`} `)}
            {config.new_column_name && ` → "${config.new_column_name}"`}
          </p>
        </div>
      )}

      {/* Actions */}
      <MissingChoice reasons={[config.columns.length < 2 && t('concat.needTwo'), !config.new_column_name.trim() && t('concat.needName')]} />

      <div className="flex justify-end gap-3 pt-4 border-t border-border">
        <button onClick={onCancel} className="btn-secondary" data-testid="concat-cancel">
          {t('form.cancel')}
        </button>
        <button
          onClick={handleApply}
          disabled={applying || config.columns.length < 2 || !config.new_column_name.trim()}
          className="btn-primary flex items-center gap-2"
          data-testid="concat-apply"
        >
          {applying && <Loader2 className="h-4 w-4 animate-spin" />}
          {t('concat.apply')}
        </button>
      </div>
    </div>
  );
};

export default ConcatColumnsOperation;
